import { BaseParser } from '../BaseParser';

/**
 * American Express Parser
 * Handles Amex CSV export format
 */
export class AmexParser extends BaseParser {
  constructor() {
    super();
    this.name = 'AmexParser';
    this.priority = 10;
  }

  /**
   * Detect if this is an Amex CSV file
   */
  detect(header, bank) {
    let confidence = 0;

    // Bank hint
    if (bank === 'amex' || bank === 'american express') {
      confidence += 50;
    }

    // Amex simple format: Date, Description, Amount (3-column)
    if (header === 'date,description,amount') {
      confidence += 50;
    }

    // Amex detailed format: Date, Description, Amount, Extended Details, ... (11-column)
    if (header.includes('date,description,amount,extended details,appears on your statement as')) {
      confidence += 50;
    }

    return confidence;
  }

  /**
   * Parse Amex CSV
   * Supports two formats:
   * 1. Simple: Date, Description, Amount (3 columns)
   * 2. Detailed: Date, Description, Amount, Extended Details, Appears On Statement, Address, City/State, Zip, Country, Reference, Category (11 columns)
   * Note: Amex uses opposite sign convention - positive = charge, negative = credit
   */
  parse(lines, filename) {
    const transactions = [];

    // Detect format from header
    const header = lines[0].toLowerCase();
    const isDetailedFormat = header.includes('extended details');

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      try {
        const row = this.parseCSVLine(line);
        if (row.length < 3) continue;

        let date, description, amount, extendedDetails, statementName, reference;

        if (isDetailedFormat && row.length >= 11) {
          // Detailed format: date,description,amount,extended details,appears on your statement as,address,city/state,zip code,country,reference,category
          [date, description, amount, extendedDetails, statementName, , , , , reference] = row;

          // Use "appears on statement as" if available, otherwise use description
          description = statementName && statementName.trim() ? statementName : description;
        } else {
          // Simple format: date,description,amount
          [date, description, amount] = row;
        }

        const rawAmount = this.parseAmount(amount);

        // Amex format: positive = charge (expense), negative = credit/payment
        // Flip the sign so expenses are negative and credits are positive
        const amountValue = -rawAmount;

        // Determine transaction type based on description and original amount
        const type = this.inferType(description, rawAmount);

        // Build memo from extended details if available
        // Include reference in memo if available (for display/debugging, not deduplication)
        let memoText = isDetailedFormat && extendedDetails ? extendedDetails : '';
        if (reference && reference.trim()) {
          memoText = memoText ? `${memoText} [Ref: ${reference}]` : `Ref: ${reference}`;
          console.log(`[AmexParser] 📝 Detailed format with reference: "${reference}"`);
        }

        // NOTE: We DON'T use reference in dedupeId because:
        // 1. Basic format exports don't have reference column
        // 2. Users may mix basic and detailed exports
        // 3. We want consistent deduplication across both formats
        // The default dedupeId (date + description + amount + bank) works for both

        transactions.push(this.createTransaction({
          id: this.generateId(date, description, amount),
          // dedupeId: Let createTransaction generate default (date+desc+amount+bank)
          date: this.parseDate(date).toISOString().split('T')[0],
          postDate: this.parseDate(date).toISOString().split('T')[0],
          description: description || 'Unknown',
          amount: amountValue,
          type: type,
          category: 'Uncategorized', // Will be handled by categorization pipeline
          memo: memoText,
          source: filename || 'Amex',
          rawData: row
        }));
      } catch (error) {
        this.log(`Error parsing line ${i}: ${error.message}`, 'error');
      }
    }

    return transactions;
  }

  /**
   * Infer transaction type for Amex
   */
  inferType(description, rawAmount) {
    const desc = description.toLowerCase();

    // Payments
    if (desc.includes('autopay payment') || desc.includes('payment - thank you') ||
        desc.includes('automatic payment')) {
      return 'Payment';
    }

    // Credits
    if (rawAmount < 0 || desc.includes('credit')) {
      return 'Credit';
    }

    // Default to sale/purchase
    return 'Sale';
  }

  getInfo() {
    return {
      name: this.name,
      priority: this.priority,
      supportedBanks: ['amex', 'american express'],
      formats: ['American Express']
    };
  }
}
